// Drives the photographic heaven sky. Each [data-cloud-speed] layer drifts sideways at that many
// px/second. Layers marked [data-cloud-loop] wrap at the distance between their first two (identical,
// edge-crossfaded) tiles so the loop is seamless; other layers wrap every 1200px. Optional [data-sun-glow] breathes.
// Prefers Web Animations; if the host pauses them (e.g. an editor preview), falls back to a
// requestAnimationFrame loop. Merchant decision: the sky moves for every visitor, including reduced-motion
// (same as the earlier starfield). Do not re-gate without asking.
const DEFAULT_LOOP_DISTANCE = 1200;
const SUN_CYCLE_S = 9;

class HeavenSkyElement extends HTMLElement {
  connectedCallback() {
    this.layers = Array.from(this.querySelectorAll('[data-cloud-speed]')).map((element) => ({
      element,
      speed: Number(element.dataset.cloudSpeed) || 12,
      distance: DEFAULT_LOOP_DISTANCE,
    }));
    this.suns = Array.from(this.querySelectorAll('[data-sun-glow]'));
    this.animations = [];
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.restart(), 200);
    };
    window.addEventListener('resize', this.onResize);
    requestAnimationFrame(() => this.restart());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    this.stop();
  }

  measure() {
    for (const layer of this.layers) {
      if (layer.element.hasAttribute('data-cloud-loop')) {
        // Tiles may overlap (crossfaded edges), so wrap at the distance between the first two tiles.
        const [first, second] = layer.element.children;
        const gap = first && second ? second.getBoundingClientRect().left - first.getBoundingClientRect().left : 0;
        layer.distance = gap > 0 ? gap : DEFAULT_LOOP_DISTANCE;
      } else {
        layer.distance = DEFAULT_LOOP_DISTANCE;
      }
    }
  }

  restart() {
    this.stop();
    this.measure();

    if (typeof this.animate === 'function') {
      this.startCompositorAnimations();
      // Verify the animations actually advance; some hosts pause them.
      this.checkTimer = setTimeout(() => {
        const first = this.animations[0];
        const advanced = first && Number(first.currentTime) > 100 && first.playState === 'running';
        if (!advanced) this.startFrameLoop();
      }, 600);
    } else {
      this.startFrameLoop();
    }
  }

  stop() {
    clearTimeout(this.checkTimer);
    cancelAnimationFrame(this.frame);
    for (const animation of this.animations || []) animation.cancel();
    this.animations = [];
    for (const layer of this.layers || []) layer.element.style.transform = '';
    for (const element of this.suns || []) {
      element.style.transform = '';
      element.style.opacity = '';
    }
  }

  startCompositorAnimations() {
    for (const layer of this.layers) {
      const duration = (layer.distance / layer.speed) * 1000;
      this.animations.push(
        layer.element.animate(
          [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(-${layer.distance}px, 0, 0)` }],
          { duration, iterations: Infinity, easing: 'linear' },
        ),
      );
    }
    for (const element of this.suns) {
      this.animations.push(
        element.animate(
          [
            { opacity: 0.8, transform: 'scale(1)' },
            { opacity: 1, transform: 'scale(1.08)' },
          ],
          { duration: (SUN_CYCLE_S / 2) * 1000, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' },
        ),
      );
    }
  }

  startFrameLoop() {
    for (const animation of this.animations) animation.cancel();
    this.animations = [];
    this.startTime = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  tick = (now) => {
    const seconds = (now - this.startTime) / 1000;
    const wave = 0.5 - 0.5 * Math.cos((seconds / SUN_CYCLE_S) * Math.PI * 2);
    for (const element of this.suns) {
      element.style.opacity = String(0.8 + 0.2 * wave);
      element.style.transform = `scale(${1 + 0.08 * wave})`;
    }
    for (const layer of this.layers) {
      const offset = (seconds * layer.speed) % layer.distance;
      layer.element.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }
    this.frame = requestAnimationFrame(this.tick);
  };
}

if (!customElements.get('s-heaven-sky')) {
  customElements.define('s-heaven-sky', HeavenSkyElement);
}
